import { Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LucideAngularModule, Check, History, Plus, TriangleAlert } from 'lucide-angular';

import { CurrencyService } from '../../../../core/services/currency.service';
import { ExchangeRateDto } from '../../../../core/models/currency.models';

/**
 * Valyuta sozlamalari: qaysi valyutalar yoqilgan, kotirovka valyutasi va kurslar.
 *
 * Kurs jadvali <b>append-only</b> — tahrirlash yoki o'chirish yo'q. Har o'zgarish
 * yangi qator bo'ladi va tarixda kim/qachon qo'ygani ko'rinib turadi.
 */
@Component({
  selector: 'app-settings-currency',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, LucideAngularModule],
  templateUrl: './currency.component.html',
})
export class SettingsCurrencyComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  readonly currencySvc = inject(CurrencyService);

  Check = Check;
  History = History;
  Plus = Plus;
  TriangleAlert = TriangleAlert;

  savingCurrencies = signal(false);
  savingRate = signal(false);
  historyOpen = signal(false);
  history = signal<ExchangeRateDto[]>([]);

  /** Yoqilgan valyutalar — foydalanuvchi belgilagan holat (saqlangunicha lokal). */
  selected = signal<Set<string>>(new Set());
  quote = signal<string>('UZS');

  // ── Yangi kurs formasi ──────────────────────────────────────────────────────
  rateFrom = signal<string>('');
  rateTo = signal<string>('');
  rateValue = signal<number | null>(null);

  readonly allCurrencies = computed(() => this.currencySvc.settings()?.currencies ?? []);
  readonly rates = computed(() => this.currencySvc.currentRates());

  /**
   * Kurs kiritish uchun tanlash mumkin bo'lgan valyutalar — faqat yoqilganlari.
   * Belgilangan (hali saqlanmagan) holatni emas, SAQLANGAN holatni ishlatadi:
   * saqlanmagan valyutaga kurs qo'yib bo'lmaydi.
   */
  readonly ratableCurrencies = computed(() => this.currencySvc.activeCurrencies());

  /** `1 USD = 12 650 UZS` — yo'nalish har doim to'liq matn bilan ko'rsatiladi. */
  readonly ratePreview = computed(() => {
    const from = this.rateFrom();
    const to = this.rateTo();
    const value = this.rateValue();
    if (!from || !to || !value) return '';
    return `1 ${from} = ${this.currencySvc.formatValue(value, to)} ${to}`;
  });

  ngOnInit(): void {
    this.syncFromSettings();
  }

  private syncFromSettings(): void {
    const settings = this.currencySvc.settings();
    if (!settings) return;
    this.selected.set(new Set(settings.currencies.filter((c) => c.isActive).map((c) => c.code)));
    this.quote.set(settings.quoteCurrency);
    if (!this.rateFrom()) {
      const foreign = settings.currencies.find((c) => c.isActive && !c.isQuoteCurrency);
      this.rateFrom.set(foreign?.code ?? '');
      this.rateTo.set(settings.quoteCurrency);
    }
  }

  isSelected(code: string): boolean {
    return this.selected().has(code);
  }

  toggle(code: string): void {
    // Kotirovka valyutasini o'chirib bo'lmaydi — kurslar unga nisbatan kiritiladi.
    if (code === this.quote()) return;
    const next = new Set(this.selected());
    if (next.has(code)) next.delete(code);
    else next.add(code);
    this.selected.set(next);
  }

  setQuote(code: string): void {
    this.quote.set(code);
    const next = new Set(this.selected());
    next.add(code);
    this.selected.set(next);
  }

  saveCurrencies(): void {
    this.savingCurrencies.set(true);
    this.currencySvc
      .updateSettings({
        activeCurrencies: [...this.selected()],
        quoteCurrency: this.quote(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Sozlamalarni qayta yuklaymiz — `money` pipe'lari shu ma'lumotga tayanadi.
          this.currencySvc
            .load()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.syncFromSettings();
              this.savingCurrencies.set(false);
              this.currencySvc.showUpdateSuccess();
            });
        },
        error: (err) => {
          this.savingCurrencies.set(false);
          this.currencySvc.showUpdateError(err);
        },
      });
  }

  saveRate(): void {
    const from = this.rateFrom();
    const to = this.rateTo();
    const value = this.rateValue();

    if (!from || !to || from === to || !value || value <= 0) {
      this.currencySvc.showWarn(this.translate.instant('SETTINGS.CURRENCY.RATE_INVALID'));
      return;
    }

    this.savingRate.set(true);
    this.currencySvc
      .setRate({ fromCurrency: from, toCurrency: to, rate: value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.rateValue.set(null);
          this.currencySvc
            .load()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.savingRate.set(false);
              this.currencySvc.showCreateSuccess();
              if (this.historyOpen()) this.loadHistory();
            });
        },
        error: (err) => {
          this.savingRate.set(false);
          this.currencySvc.showCreateError(err);
        },
      });
  }

  toggleHistory(): void {
    this.historyOpen.update((v) => !v);
    if (this.historyOpen()) this.loadHistory();
  }

  private loadHistory(): void {
    this.currencySvc
      .getRateHistory()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.history.set(res.data ?? []),
        error: (err) => this.currencySvc.showLoadError(err),
      });
  }
}
